import * as React from 'react';
import { Typography as MuiTypography, TypographyProps as MuiTypographyProps } from '@mui/material';
import { typographyColor, typographyVariant, uppercaseTypography } from './typographyCssDefs';
import { useTheme } from '@emotion/react';

export type TypographyVariant =
  | 'display1Bold'
  | 'display1Medium'
  | 'display2Bold'
  | 'display2Medium'
  | 'heading1Bold'
  | 'heading1Medium'
  | 'heading2Bold'
  | 'heading2Medium'
  | 'body1Bold'
  | 'body1Medium'
  | 'body1Regular'
  | 'body2Bold'
  | 'body2Medium'
  | 'body2Regular'
  | 'caption1Medium'
  | 'caption1Regular'
  | 'caption2Medium'
  | 'caption2Regular'
  | 'inheritFont'
  | 'srOnly';

export type TypographyColor =
  | 'white'
  | 'lighter'
  | 'light'
  | 'base'
  | 'dark'
  | 'danger'
  | 'primary'
  | 'success'
  | 'warning'
  | 'inherit';

export interface TypographyProps
  extends Pick<MuiTypographyProps, Exclude<keyof MuiTypographyProps, 'color' | 'variant'>> {
  color?: TypographyColor;
  variant?: TypographyVariant;
  component?: React.ElementType;
  mb?: number;
  uppercase?: boolean;
}

type VariantMap = { [k in TypographyVariant]?: string };

// defaults, can be changed with the component prop
const variantMapping: VariantMap = {
  display1Bold: 'h1',
  display1Medium: 'h1',
  display2Bold: 'h2',
  display2Medium: 'h2',
  heading1Bold: 'h3',
  heading1Medium: 'h3',
  heading2Bold: 'h4',
  heading2Medium: 'h4',
  body1Bold: 'p',
  body1Medium: 'p',
  body1Regular: 'p',
  body2Bold: 'p',
  body2Medium: 'p',
  body2Regular: 'p',
  caption1Medium: 'p',
  caption1Regular: 'p',
  caption2Medium: 'p',
  caption2Regular: 'p',
};

/**
 * Use `Typography` for text
 *
 * ```js
 * import { Typography, TypographyProps } from 'ui';
 * ```
 *
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const Typography = React.forwardRef<React.ElementType, TypographyProps>(
  (
    {
      color = 'base',
      variant = 'body1Regular',
      uppercase = false,
      className,
      component,
      paragraph,
      children,
      mb = 0,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const marginBottom = paragraph ? theme.spacing(2) : mb ? theme.spacing(mb) : undefined;

    const componentProp = component || (paragraph ? 'p' : variantMapping[variant]) || 'span';

    // variant mapping is mapped to the theme style, so here is any until we figure out a way to sync these 2 things up
    return (
      <MuiTypography
        style={{ marginBottom }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        component={componentProp as any} //need to figure out how to properly extend overridable component, this is here because variantMapping was not working properly
        className={className}
        css={[typographyColor(color), typographyVariant(variant), uppercase && uppercaseTypography]}
        paragraph={paragraph}
        variant="inherit" // Assures none of the MUI typography variants are applied. Without this 'body1` class is applied to each typography instance
        ref={ref}
        {...rest}
      >
        {children}
      </MuiTypography>
    );
  }
);

export default Typography;
